/**
 * Copyright 2019 CANAL+ Group
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { EMPTY, merge, of, Subject } from "rxjs";
import { map, mergeMap, reduce, scan, takeUntil, tap } from "rxjs/operators";
import findIndex from "../../../../../utils/array_find_index";
import { concat, strToBytes } from "../../../../../utils/byte_parsing";
import { createBox } from "../../../../../parsers/containers/isobmff";
import { IndexDBError } from "../../utils";
import { ContentType } from "../context/types";
/**
 * Download a segment associated with a context.
 *
 * @remarks
 * We are downloading the segment 3 by 3 for now.
 * It's something that will look soon.
 *
 * @param IContext[] - An array of context we have to download.
 * > It's possibly a number, when the segment has already been download
 * @param KeyContextType - Tell what type of buffer it is (VIDEO/AUDIO/TEXT).
 * @returns ICustomSegment - An Object of the downloaded segment.
 *
 */
export function handleSegmentPipelineFromContexts(ctxs, contentType, _a) {
    var segmentPipelinesManager = _a.segmentPipelinesManager, isInitData = _a.isInitData, nextSegments = _a.nextSegments, progress = _a.progress, type = _a.type;
    var segmentFetcherForCurrentContentType = segmentPipelinesManager.createPipeline(contentType, new Subject());
    return of.apply(void 0, ctxs).pipe(mergeMap(function (ctx, index) {
        if (Array.isArray(ctx.segment)) {
            return EMPTY;
        }
        return segmentFetcherForCurrentContentType.createRequest(ctx).pipe(mergeMap(function (evt) {
            switch (evt.type) {
                case "chunk":
                    return evt.parse();
                default:
                    return EMPTY;
            }
        }), reduce(function (acc, _a) {
            var chunkData = _a.chunkData;
            if (chunkData === null) {
                return acc;
            }
            if (contentType === ContentType.TEXT &&
                ctx.representation.mimeType !== undefined &&
                ctx.representation.mimeType === "application/mp4") {
                return concat(acc, createBox("moof", new Uint8Array(0)), 
                // May be chunkData.data for subtitles...
                createBox("mdat", strToBytes(chunkData)));
            }
            return concat(acc, chunkData);
        }, new Uint8Array(0)), map(function (chunkData) {
            if (nextSegments !== undefined && !isInitData) {
                nextSegments[index] = [
                    ctx.segment.time,
                    ctx.segment.timescale,
                    ctx.segment.duration,
                ];
            }
            return {
                chunkData: chunkData,
                progress: progress,
                type: type,
                contentType: contentType,
                ctx: ctx,
                index: index,
                isInitData: isInitData,
                nextSegments: nextSegments,
                representationID: ctx.representation.id,
            };
        }));
    }, 3 // TODO: See If we limit the number of concurrent request at the same time.
    ));
}
/**
 * An Util function that abstract a redundant operation that consist
 * to create the different context depending a segment.
 *
 * @param IContextRicher[] - Array of IContext with few field added to it.
 * @param KeyContextType - Tell what type of buffer it is (VIDEO/AUDIO/TEXT).
 * @param IAbstractContextCreation - Usefull arguments we need to build
 * the IContext array.
 * @returns ICustomSegment - An Object of the downloaded segment.
 *
 */
function handleAbstractSegmentPipelineContextFor(contextsRicher, contentType, _a) {
    var type = _a.type, progress = _a.progress, segmentPipelinesManager = _a.segmentPipelinesManager, manifest = _a.manifest;
    return of.apply(void 0, contextsRicher).pipe(mergeMap(function (contextRicher) {
        var nextSegments = contextRicher.nextSegments, ctx = __rest(contextRicher, ["nextSegments"]);
        return handleSegmentPipelineFromContexts(nextSegments.map(function (segment) { return (__assign(__assign({}, ctx), { segment: segment, manifest: manifest })); }), contentType, {
            type: type,
            progress: progress,
            nextSegments: nextSegments,
            segmentPipelinesManager: segmentPipelinesManager,
            isInitData: false,
        });
    }));
}
/**
 * The top level function downloader that should start the pipeline of
 * download for each buffer type (VIDEO/AUDIO/TEXT).
 *
 * @remarks
 * - It also store each segment downloaded in IndexDB.
 * - Add the segment in the ProgressBarBuilder.
 * - Emit a global progress when a segment has been downloaded.
 * - Eventually, wait an event of the pause$ Subject to put the download in pause.
 *
 * @param Observable<IInitGroupedSegments> - A Observable that carry
 * all the data we need to start the download.
 * @param IManifestDBState - The current builder state of the download.
 * @param IUtils - Usefull tools to store/emit/pause the current content of the download.
 * @returns IManifestDBState - The state of the Manifest at the X time in the download.
 *
 */
export function segmentPipelineDownloader$(builderObs$, builderInit, _a) {
    var contentID = _a.contentID, emitter = _a.emitter, pause$ = _a.pause$, db = _a.db;
    return builderObs$.pipe(mergeMap(function (_a) {
        var video = _a.video, audio = _a.audio, text = _a.text, segmentPipelinesManager = _a.segmentPipelinesManager, manifest = _a.manifest, progress = _a.progress, type = _a.type;
        if (manifest == null || segmentPipelinesManager == null) {
            return EMPTY;
        }
        return merge(handleAbstractSegmentPipelineContextFor(video, ContentType.VIDEO, {
            type: type,
            progress: progress,
            segmentPipelinesManager: segmentPipelinesManager,
            manifest: manifest,
        }), handleAbstractSegmentPipelineContextFor(audio, ContentType.AUDIO, {
            type: type,
            progress: progress,
            segmentPipelinesManager: segmentPipelinesManager,
            manifest: manifest,
        }), handleAbstractSegmentPipelineContextFor(text, ContentType.TEXT, {
            type: type,
            progress: progress,
            segmentPipelinesManager: segmentPipelinesManager,
            manifest: manifest,
        }));
    }), tap(function (_a) {
        var chunkData = _a.chunkData, representationID = _a.representationID, time = _a.ctx.segment.time, contentType = _a.contentType;
        db.put("segments", {
            contentID: contentID,
            segmentKey: time + "--" + representationID + "--" + contentID,
            data: chunkData,
            size: chunkData.byteLength,
        }).catch(function (err) {
            throw new IndexDBError("\n          " + contentID + ": Impossible\n          to store the current segment (" + contentType + ") at " + time + ": " + err.message + "\n        ");
        });
    }), scan(function (acc, _a) {
        var progress = _a.progress, ctx = _a.ctx, contentType = _a.contentType, nextSegments = _a.nextSegments, representationID = _a.representationID, chunkData = _a.chunkData;
        if (progress !== undefined) {
            acc.progress.overall = progress.overall;
        }
        acc.progress.current += 1;
        var percentage = (acc.progress.current / acc.progress.overall) * 100;
        acc.progress.percentage =
            percentage > 98 && percentage < 100
                ? percentage
                : Math.round(percentage);
        acc.size += chunkData.byteLength;
        if (nextSegments === undefined) {
            return acc;
        }
        var indexRepresentation = findIndex(acc[contentType], function (_a) {
            var representation = _a.representation;
            return representation.id === representationID;
        });
        if (indexRepresentation === -1) {
            acc[contentType].push({
                nextSegments: nextSegments,
                period: ctx.period,
                adaptation: ctx.adaptation,
                representation: ctx.representation,
                id: representationID,
            });
            return __assign(__assign({}, acc), { manifest: ctx.manifest });
        }
        acc[contentType][indexRepresentation] = {
            nextSegments: nextSegments,
            period: ctx.period,
            adaptation: ctx.adaptation,
            representation: ctx.representation,
            id: representationID,
        };
        return __assign(__assign({}, acc), { manifest: ctx.manifest });
    }, builderInit), tap(function (_a) {
        var size = _a.size, progress = _a.progress;
        emitter.trigger("progress", {
            contentID: contentID,
            progress: progress.percentage,
            size: size,
            status: "downloading",
        });
    }), takeUntil(pause$));
}
